// 📄 components/layout/GlobalSearchModal.tsx
// Buscador global del proyecto (ícono de lupa en el navbar + Ctrl+K/Cmd+K,
// primer atajo de teclado de la plataforma) — busca en 2 capas:
//  1. Navegación (secciones/canales, ver searchIndex.ts): instantáneo, sin
//     ninguna consulta a Firestore, es filtrar un array en memoria.
//  2. Catálogos técnicos del proyecto (BAdIs/Transacciones/Programas/
//     Interfaces): se cargan UNA vez al abrir el modal (mismo patrón de
//     "cargar todo y filtrar en cliente" que ya usa SoporteHome.tsx), no en
//     cada tecla — evita re-consultar Firestore por cada letra escrita.
// Solo tiene sentido DENTRO de un proyecto (los catálogos son por
// proyecto) — por eso lee `projectId` de useParams() directamente en vez
// de useProject(): Navbar.tsx se renderiza FUERA de ProjectProvider (ver
// App.js), pero useParams() sí resuelve igual porque lee del Route más
// cercano que matchea, sin depender del contexto de proyecto.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Search, X, ExternalLink, RefreshCw } from 'lucide-react';
import { IconoDe } from '../../pages/Dashboard/menuConfig.tsx';
import { buildNavIndex, NavSearchResult } from '../../pages/Dashboard/searchIndex.ts';
import { getBadisZ, BadiZ } from '../../services/badiZService.ts';
import { getTransaccionesZ, TransaccionZ } from '../../services/transaccionZService.ts';
import { getProgramasZ, ProgramaZ } from '../../services/programaZService.ts';
import { getInterfacesZ, InterfazZ } from '../../services/interfazZService.ts';

const MAX_POR_GRUPO = 6;

interface Catalogos {
  badis: BadiZ[];
  transacciones: TransaccionZ[];
  programas: ProgramaZ[];
  interfaces: InterfazZ[];
}

const coincide = (query: string, ...campos: (string | undefined)[]): boolean =>
  campos.some((c) => c?.toLowerCase().includes(query));

export default function GlobalSearchModal() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [catalogos, setCatalogos] = useState<Catalogos | null>(null);
  const [loadingCatalogos, setLoadingCatalogos] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k' && projectId) {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [projectId]);

  // Si el usuario navega a OTRO proyecto, el caché de catálogos de la
  // instancia anterior queda obsoleto (GlobalSearchModal vive montado una
  // sola vez en el Navbar, persiste entre navegaciones) — se limpia acá
  // para forzar una recarga la próxima vez que se abra el modal.
  useEffect(() => { setCatalogos(null); }, [projectId]);

  useEffect(() => {
    if (!open || !projectId || catalogos || loadingCatalogos) return;
    setLoadingCatalogos(true);
    Promise.all([getBadisZ(projectId), getTransaccionesZ(projectId), getProgramasZ(projectId), getInterfacesZ(projectId)])
      .then(([badis, transacciones, programas, interfaces]) => setCatalogos({ badis, transacciones, programas, interfaces }))
      .catch((err) => console.error('Error cargando catálogos para el buscador:', err))
      .finally(() => setLoadingCatalogos(false));
  }, [open, projectId, catalogos, loadingCatalogos]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
    else { setQuery(''); }
  }, [open]);

  const navIndex = useMemo(() => (projectId ? buildNavIndex(projectId) : []), [projectId]);

  const q = query.trim().toLowerCase();
  const hayQuery = q.length > 0;

  const navResults: NavSearchResult[] = useMemo(
    () => (hayQuery ? navIndex.filter((n) => n.label.toLowerCase().includes(q)).slice(0, MAX_POR_GRUPO) : []),
    [navIndex, q, hayQuery]
  );

  const badiResults = useMemo(
    () => (hayQuery && catalogos ? catalogos.badis.filter((b) => coincide(q, b.nombre, b.clase, b.interfaz, b.descripcion, b.notas)).slice(0, MAX_POR_GRUPO) : []),
    [catalogos, q, hayQuery]
  );
  const transResults = useMemo(
    () => (hayQuery && catalogos ? catalogos.transacciones.filter((t) => coincide(q, t.codigo, t.nombre, t.descripcion, t.notas)).slice(0, MAX_POR_GRUPO) : []),
    [catalogos, q, hayQuery]
  );
  const progResults = useMemo(
    () => (hayQuery && catalogos ? catalogos.programas.filter((p) => coincide(q, p.nombre, p.nombreTecnico, p.descripcion, p.notas)).slice(0, MAX_POR_GRUPO) : []),
    [catalogos, q, hayQuery]
  );
  const interResults = useMemo(
    () => (hayQuery && catalogos ? catalogos.interfaces.filter((i) => coincide(q, i.textoBreve, i.nombreTecnico, i.campoBusqueda, i.descripcion, i.notas) || i.palabrasClave.some((p) => p.toLowerCase().includes(q))).slice(0, MAX_POR_GRUPO) : []),
    [catalogos, q, hayQuery]
  );

  const totalResultados = navResults.length + badiResults.length + transResults.length + progResults.length + interResults.length;
  const sinResultados = hayQuery && !loadingCatalogos && totalResultados === 0;

  const ir = (ruta: string) => {
    navigate(ruta);
    setOpen(false);
  };

  if (!projectId) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="Buscar en el proyecto (Ctrl+K)"
        className="p-1.5 rounded-lg text-gray-300 hover:text-blue-400 transition-colors"
      >
        <Search className="w-5 h-5" />
      </button>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center pt-24 p-4" onClick={() => setOpen(false)}>
          <div
            className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-xl shadow-2xl max-h-[70vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3 p-4 border-b border-white/10 flex-shrink-0">
              <Search className="w-4 h-4 text-gray-500 flex-shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar secciones, BAdIs, transacciones, programas, interfaces..."
                className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 focus:outline-none"
              />
              {loadingCatalogos && <RefreshCw className="w-4 h-4 animate-spin text-gray-500 flex-shrink-0" />}
              <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white flex-shrink-0"><X className="w-4 h-4" /></button>
            </div>

            <div className="overflow-y-auto flex-1 p-2">
              {!hayQuery && (
                <p className="text-xs text-gray-500 text-center py-8">Escribí para buscar en la navegación y los catálogos técnicos del proyecto.</p>
              )}

              {navResults.length > 0 && (
                <ResultGroup titulo="Navegación">
                  {navResults.map((r) => (
                    <ResultItem key={r.ruta} onClick={() => ir(r.ruta)} icon={<IconoDe icon={r.icon} size={16} />} titulo={r.label} />
                  ))}
                </ResultGroup>
              )}
              {badiResults.length > 0 && (
                <ResultGroup titulo="BAdIs">
                  {badiResults.map((b) => (
                    <ResultItem key={b.id} onClick={() => ir(`/proyecto/${projectId}/badis`)} titulo={b.nombre} subtitulo={b.clase} />
                  ))}
                </ResultGroup>
              )}
              {transResults.length > 0 && (
                <ResultGroup titulo="Transacciones">
                  {transResults.map((t) => (
                    <ResultItem key={t.id} onClick={() => ir(`/proyecto/${projectId}/transacciones`)} titulo={`${t.codigo} — ${t.nombre}`} />
                  ))}
                </ResultGroup>
              )}
              {progResults.length > 0 && (
                <ResultGroup titulo="Programas">
                  {progResults.map((p) => (
                    <ResultItem key={p.id} onClick={() => ir(`/proyecto/${projectId}/programas`)} titulo={p.nombre} subtitulo={p.nombreTecnico} />
                  ))}
                </ResultGroup>
              )}
              {interResults.length > 0 && (
                <ResultGroup titulo="Interfaces">
                  {interResults.map((i) => (
                    <ResultItem key={i.id} onClick={() => ir(`/proyecto/${projectId}/interfaces`)} titulo={i.textoBreve} subtitulo={i.nombreTecnico} />
                  ))}
                </ResultGroup>
              )}

              {sinResultados && (
                <a
                  href={`https://www.google.com/search?q=${encodeURIComponent(query)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-3 py-3 rounded-lg text-sm text-blue-400 hover:bg-white/5 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" /> Buscar &quot;{query}&quot; en Google
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ResultGroup({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="mb-2">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-3 py-1.5">{titulo}</p>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function ResultItem({ onClick, icon, titulo, subtitulo }: { onClick: () => void; icon?: React.ReactNode; titulo: string; subtitulo?: string }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 text-left px-3 py-2 rounded-lg text-sm text-gray-200 hover:bg-white/5 transition-colors"
    >
      {icon && <span className="text-blue-400 flex-shrink-0">{icon}</span>}
      <span className="flex-1 min-w-0 truncate">{titulo}</span>
      {subtitulo && <span className="text-xs text-gray-500 truncate flex-shrink-0 max-w-[40%]">{subtitulo}</span>}
    </button>
  );
}
